import { ApolloClient } from '@apollo/client';
import {
  PatientAthenaLinkStatusDocument,
  PatientAthenaLinkStatusQuery,
} from 'shared/api';

export type PatientAthenaLinkStatus = NonNullable<
  PatientAthenaLinkStatusQuery['patient']
>;

export type PollPatientAthenaLinkResult =
  | {
      status: 'success';
      athenaPatientId: string;
      patient: PatientAthenaLinkStatus;
    }
  | { status: 'error'; message: string; patient?: PatientAthenaLinkStatus }
  | { status: 'not_in_progress'; patient?: PatientAthenaLinkStatus }
  | { status: 'timeout' };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const resolveLinkStatus = (
  patient: PatientAthenaLinkStatus | null | undefined
): PollPatientAthenaLinkResult | null => {
  if (!patient) {
    return { status: 'timeout' };
  }

  if (patient.athenaPatientCreateError) {
    return {
      status: 'error',
      message: patient.athenaPatientCreateError,
      patient,
    };
  }

  if (patient.athena_patient_id) {
    return {
      status: 'success',
      athenaPatientId: patient.athena_patient_id,
      patient,
    };
  }

  if (!patient.athenaPatientCreateInProgress) {
    return { status: 'not_in_progress', patient };
  }

  return null;
};

export const queryPatientAthenaLinkStatus = async (
  client: ApolloClient<object>,
  patientId: string
): Promise<PatientAthenaLinkStatus | null> => {
  const { data } = await client.query<PatientAthenaLinkStatusQuery>({
    query: PatientAthenaLinkStatusDocument,
    variables: { id: patientId },
    fetchPolicy: 'network-only',
  });

  return data?.patient ?? null;
};

export const pollPatientAthenaLink = async (
  client: ApolloClient<object>,
  patientId: string
): Promise<PollPatientAthenaLinkResult> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  const poll = async (): Promise<PollPatientAthenaLinkResult> => {
    const patient = await queryPatientAthenaLinkStatus(client, patientId);
    const resolved = resolveLinkStatus(patient);

    if (resolved) {
      return resolved;
    }

    if (Date.now() >= deadline) {
      return { status: 'timeout' };
    }

    await sleep(POLL_INTERVAL_MS);
    return poll();
  };

  return poll();
};

export const waitForPatientAthenaLinkIfInProgress = async (
  client: ApolloClient<object>,
  patientId: string
): Promise<PollPatientAthenaLinkResult> => {
  const patient = await queryPatientAthenaLinkStatus(client, patientId);
  const resolved = resolveLinkStatus(patient);

  if (resolved) {
    return resolved;
  }

  return pollPatientAthenaLink(client, patientId);
};
